import React, { SyntheticEvent } from 'react';

import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';

import { SigninButton } from 'types/provider';

import { Modal, Form, Divider, Button, FormField } from 'semantic';

import { useModalContext } from 'components/modal/ModalContext';
import * as Yup from 'yup';
import InputField from 'components/form-fields/formik/InputField';
import SelectField from 'components/form-fields/formik/SelectField';

import { supportedLanguages } from 'i18n';
import DropdownField from 'components/form-fields/formik/DropdownField';

const ButtonSchema = Yup.object({
  type: Yup.string()
    .oneOf(['email-password', 'sso', 'saml', 'custom-url'])
    .required(),
  label: Yup.object().shape({
    en: Yup.string().required(),
  }),
  url: Yup.string().when('type', {
    is: 'custom-url',
    then: (schema) => schema.required(),
  }),
  issuer: Yup.string().when('type', {
    is: 'sso',
    then: (schema) => schema.required(),
  }),
  accountId: Yup.string().when('type', {
    is: 'saml',
    then: (schema) => schema.required(),
  }),
});

export function EditSignInButton({
  ssoAccounts,
  socialLoginIssuersIds,
  initialData,
  hasEmailPasswordButton = false,
  onChange,
}: {
  ssoAccounts: { _id: string; name: string }[];
  onChange: (button: SigninButton) => void;
  hasEmailPasswordButton?: boolean;
  initialData?: SigninButton;
  providerId: string;
  socialLoginIssuersIds: string[];
}) {
  const { closeModal } = useModalContext();
  const { t } = useTranslation();

  const buttonTypeOptions = [
    {
      key: 'email-password',
      value: 'email-password',
      text: t('signinSettingsButton.emailPassword', 'Email Password'),
      disabled: hasEmailPasswordButton,
    },
    {
      key: 'sso',
      value: 'sso',
      text: t('signinSettingsButton.sso', 'Single Sign-On (SSO)'),
      disabled: socialLoginIssuersIds.length === 0,
    },
    {
      key: 'saml',
      value: 'saml',
      text: t('signinSettingsButton.saml', 'SAML'),
      disabled: ssoAccounts.length === 0,
    },
    {
      key: 'custom-url',
      value: 'custom-url',
      text: t('signinSettingsButton.customUrl', 'Custom URL'),
    },
  ].filter(Boolean);

  const initialFormData: SigninButton = {
    type:
      initialData?.type ||
      (hasEmailPasswordButton ? 'custom-url' : 'email-password'),
    label: initialData?.label || {},
    issuer: initialData?.issuer,
    accountId: initialData?.accountId,
    url: initialData?.url,
  };

  const handleSubmit = async (values: SigninButton) => {
    onChange(values);
    closeModal();
  };

  return (
    <>
      <Modal.Header>
        {initialData
          ? t('signinSettingsButton.editButton', 'Edit Sign In Button')
          : t('signinSettingsButton.addButton', 'Add Sign In Button')}
      </Modal.Header>
      <Modal.Content>
        <Formik
          initialValues={initialFormData}
          validationSchema={ButtonSchema}
          onSubmit={handleSubmit}>
          {({
            values,
            errors,
            touched,
            setFieldValue,
            handleSubmit,
            resetForm,
          }) => (
            <Form id="editsignupbtn" onSubmit={handleSubmit}>
              {!initialData && (
                <FormField>
                  <label>{t('signinSettingsButton.type', 'Type')}</label>
                  <SelectField
                    selection
                    name="type"
                    options={buttonTypeOptions}
                    onChange={(e, { value }) => {
                      resetForm({
                        values: {
                          ...initialFormData,
                          type: value as SigninButton['type'],
                        },
                      });
                    }}
                    value={values.type}
                    placeholder={t(
                      'signinSettingsButton.selectType',
                      'Select Type'
                    )}
                  />
                </FormField>
              )}

              {values.type === 'saml' && (
                <>
                  <p>
                    {t(
                      'signinSettingsButton.selectSamlAccount',
                      'Select an account with SAML configuration'
                    )}
                  </p>
                  <DropdownField
                    name="accountId"
                    required
                    placeholder={t(
                      'signinSettingsButton.selectAccount',
                      'Select Account'
                    )}
                    label={t(
                      'signinSettingsButton.samlAccountLabel',
                      'SAML Account'
                    )}
                    selection
                    options={ssoAccounts.map((c) => {
                      return {
                        key: `account-${c._id}`,
                        value: c._id,
                        text: c.name,
                      };
                    })}
                  />
                </>
              )}

              {values.type === 'sso' && (
                <>
                  <p>
                    {t(
                      'signinSettingsButton.selectSsoIssuer',
                      'Select a single sign-on issuer'
                    )}
                  </p>
                  <FormField required>
                    <label>
                      {t('signinSettingsButton.issuerLabel', 'Issuer')}
                    </label>
                    <SelectField
                      name="issuer"
                      selection
                      options={socialLoginIssuersIds.map((issuer) => {
                        return {
                          key: `issuer-${issuer}`,
                          value: issuer,
                          text: issuer,
                        };
                      })}
                      placeholder={t(
                        'signinSettingsButton.selectIssuer',
                        'Select Issuer'
                      )}
                    />
                  </FormField>
                </>
              )}

              {values.type === 'custom-url' && (
                <>
                  <p>
                    {t(
                      'signinSettingsButton.customUrlDescription',
                      'You can enter a custom URL for the sign-in button.'
                    )}
                  </p>
                  <FormField>
                    <label>URL</label>
                    <InputField
                      type="url"
                      name="url"
                      placeholder={t(
                        'signinSettingsButton.customUrlPlaceholder',
                        'Enter the custom URL (e.g., https://example.com/login)'
                      )}
                    />
                  </FormField>
                </>
              )}

              <Divider />
              <Form.Field error={touched.label && !!errors.label}>
                <label>{t('signinSettingsButton.labels', 'Labels')}</label>
                {supportedLanguages.map((lang) => (
                  <Form.Field key={lang.value} style={{ marginBottom: '12px' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.9em',
                      }}>
                      {lang.text}
                    </label>
                    <Form.Input
                      value={values.label[lang.value] || ''}
                      onChange={(e: SyntheticEvent, { value }) =>
                        setFieldValue(`label.${lang.value}`, value as string)
                      }
                      placeholder={t(
                        'signinSettingsButton.labelPlaceholder',
                        'Enter label (e.g., "Sign in with Google")'
                      )}
                    />
                  </Form.Field>
                ))}
              </Form.Field>
            </Form>
          )}
        </Formik>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => closeModal()}>Cancel</Button>
        <Button form="editsignupbtn" primary type="submit">
          {initialData
            ? t('signinSettingsButton.submitButton', 'Update Button')
            : t('signinSettingsButton.addButton', 'Add Button')}
        </Button>
      </Modal.Actions>
    </>
  );
}
